import { DEFAULT_NODE_HEIGHT } from '@constants/common.const';
import {
  BoardModel,
  NodeModel,
  BoardConfig,
} from '@models/board-config.interface';
import { NodeLayoutItem, BoardLayout, BoardLayoutConfig } from '@models/layout';
import { Singleton } from '@utils/singleton';
import { ActionType } from '@enums/node-types.enum';
import { getStrategy } from './strategies';

@Singleton
export class BoardService {
  buildBoard(boardConfig: BoardConfig): BoardModel {

    if (!boardConfig) {
      console.log(`boardconfig cannot be null in buildBoard`);
      return {
        length: 0,
        nodes: []
      };
    }

    const length = Math.max(1, Math.floor(boardConfig.length));

    // Start all nodes as normal
    const normalSize = this.getNodeSize(ActionType.Normal);
    const nodes: NodeModel[] = Array.from({ length }, (_, i) => ({
      index: i + 1,
      type: ActionType.Normal,
      width: normalSize.width,
      height: normalSize.height,
    }));

    // Set action nodes
    if (boardConfig.nodes) {
      for (const [k, v] of Object.entries(boardConfig.nodes)) {
        const idx = Number(k);
        if (!Number.isFinite(idx) || idx < 1 || idx > length) continue;
        const type = v.actionType ?? ActionType.Normal;
        const { width, height } = this.getNodeSize(type);
        nodes[idx - 1] = { index: idx, type, width, height, action: v };
      }
    }

    return { length, nodes };
  }

  getNodeSize(type: ActionType): { width: number; height: number } {
    return getStrategy(type).getSize();
  }

  createWaveLayout(
    model: BoardModel,
    canvasWidth: number,
    canvasHeight: number,
    opts: BoardLayoutConfig,
  ): BoardLayout {
    const gapY = opts.gapY;
    const marginTop = opts.marginTop;
    const marginBottom = opts.marginBottom;
    const settings = opts.settings ?? {};
    const centerX = canvasWidth / 2;

    // Calculate Content Height
    // Total height needed for all nodes + gaps + top/bottom margins
    const nodesHeight = model.nodes.reduce((acc, n, i) => {
      const gap = i === model.nodes.length - 1 ? 0 : gapY;
      return acc + n.height + gap;
    }, 0);
    const contentHeight = nodesHeight + marginTop + marginBottom;

    // get settings from url during setting up the layout (ADMIN feature)
    const qp =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search)
        : null;
    const qNum = (k: string): number | undefined => {
      const v = qp?.get(k);
      const n = v != null ? Number(v) : NaN;
      return Number.isFinite(n) ? n : undefined;
    };

    const paddingX = qNum('paddingX') ?? settings.paddingX ?? 16;
    const sceneHeight = Math.max(canvasHeight, contentHeight);
    const maxAmp = Math.max(
      0,
      canvasWidth / 2 - DEFAULT_NODE_HEIGHT / 2 - paddingX,
    );
    const amplitude = Math.min(
      qNum('amplitude') ?? settings.amplitude ?? Math.floor(canvasWidth * 0.45),
      maxAmp,
    );
    const waves = qNum('waves') ?? settings.waves ?? 1.8;
    const phase = qNum('phase') ?? settings.phase ?? 0;
    const curvature = qNum('curvature') ?? settings.curvature ?? 1; // the higher the more chaotic/entropy -- 0 = pure sine, >1 = more chaotic spline
    const stretch = qNum('stretch') ?? settings.stretch ?? 1; // gap between the tiles (think it as a snake)
    const horizontalNoise =
      qNum('horizontalNoise') ?? settings.horizontalNoise ?? 0; // horizontal jitter (be aware that this include randomic effects) 0..1+ of amplitude
    const verticalNoise = qNum('verticalNoise') ?? settings.verticalNoise ?? 0; // same as horizontal jitter but vertical :p, 0..1+ of gapY
    const twist = qNum('twist') ?? settings.twist ?? 0; // make curves more concentrated and breaks plauteous
    const startY = sceneHeight - marginBottom - DEFAULT_NODE_HEIGHT / 2;

    // pseudo random deterministic (this formula is not 100% proven and there is margin to enhance this)
    const hash01 = (i: number, salt: number): number => {
      const v = Math.sin(i * 12.9898 + salt * 78.233) * 43758.5453;
      return v - Math.floor(v);
    };

    // spline through them gives a smooth but irregular path.
    // PS: consider sin if the params won't have any use in the future
    const K = Math.max(3, Math.floor(waves * 2 + 2));
    const anchors: number[] = [];
    for (let k = 0; k < K; k++) {
      const u = K === 1 ? 0 : k / (K - 1);
      const sinePart = Math.sin(u * Math.PI * 2 * waves + phase) * amplitude;
      const twistPart =
        twist *
        amplitude *
        0.4 *
        Math.sin(u * Math.PI * 2 * waves * 2.7 + phase * 1.7);
      const wobble = (hash01(k, 7) - 0.5) * amplitude * curvature;
      anchors.push(sinePart + twistPart + wobble);
    }

    // Catmull-Rom interpolation at t in <0 - 1>
    const cr = (p0: number, p1: number, p2: number, p3: number, t: number) => {
      const t2 = t * t;
      const t3 = t2 * t;
      return (
        0.5 *
        (2 * p1 +
          (-p0 + p2) * t +
          (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
          (-p0 + 3 * p1 - 3 * p2 + p3) * t3)
      );
    };
    const splineX = (t: number): number => {
      if (anchors.length === 1) return anchors[0];
      const segs = anchors.length - 1;
      const seg = Math.min(Math.floor(t * segs), segs - 1);
      const localT = t * segs - seg;
      const p0 = anchors[Math.max(0, seg - 1)];
      const p1 = anchors[seg];
      const p2 = anchors[seg + 1];
      const p3 = anchors[Math.min(segs, seg + 2)];
      return cr(p0, p1, p2, p3, localT);
    };

    const nodes: NodeLayoutItem[] = [];
    let currentY = startY;

    for (let i = 0; i < model.length; i++) {
      const t = model.length === 1 ? 0 : i / (model.length - 1);
      const nodeType = model.nodes[i].type;
      const nodeHeight = model.nodes[i].height;

      const xNoise = (hash01(i, 11) - 0.5) * amplitude * horizontalNoise;
      const yNoise = (hash01(i, 13) - 0.5) * gapY * verticalNoise;

      const rawX = centerX + splineX(t) + xNoise;
      const clampedX = Math.min(
        Math.max(rawX, paddingX + DEFAULT_NODE_HEIGHT / 2),
        canvasWidth - paddingX - DEFAULT_NODE_HEIGHT / 2,
      );
      nodes.push({
        index: i + 1,
        x: Math.round(clampedX),
        y: Math.round(currentY + yNoise),
        side: canvasWidth - clampedX < canvasWidth / 2 ? 'right' : 'left',
        type: nodeType,
        action: model.nodes[i].action,
      });

      if (i < model.length - 1) {
        const nextHeight = model.nodes[i + 1].height;
        const deltaY = (nodeHeight / 2 + nextHeight / 2 + gapY) * stretch;
        currentY -= deltaY;
      }
    }

    const minY =
      Math.min(...nodes.map((p, idx) => p.y - model.nodes[idx].height / 2)) -
      marginTop;
    const maxY =
      Math.max(...nodes.map((p, idx) => p.y + model.nodes[idx].height / 2)) +
      marginBottom;

    // Shift everything so minY >= 0 (world coordinates shouldn't be negative)
    const worldShift = minY < 0 ? -minY : 0;
    if (worldShift !== 0) {
      for (const p of nodes) p.y = Math.round(p.y + worldShift);
    }

    const worldHeight = Math.max(canvasHeight, Math.ceil(maxY + worldShift));

    // Start camera at the bottom so node 1 is visible even when worldHeight > screenHeight
    const initialScrollY = Math.max(0, worldHeight - canvasHeight);

    return {
      nodes,
      nodeSize: 0,
      rows: model.length,
      cols: 1,
      originX: 0,
      originY: 0,
      worldHeight,
      initialScrollY,
    };
  }
}
